/**
 * `buildOutcomeSuggestionRowFromEvent` — event-as-row builder for the
 * `useGetOutcomeSuggestions` live list.
 *
 * `morfi.v1.cmd.OutcomeSuggestionEvent` carries every field a list row
 * needs (label, ticker, imageUrl, reason, scores) AND the suggester's
 * resolved identity (name, handle, avatar, provider badges) — the proto
 * mirrors the REST list-item shape (per
 * `backend/backend-api-service/src/features/markets/outcome-suggestions/`),
 * so this helper is a pure projection — no refetch needed. The prepended
 * row therefore renders the suggester's name/handle/avatar immediately,
 * not a bare wallet address.
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

type BadgeProvider = 'x' | 'google' | 'apple';

interface ProviderBadgeShape {
  readonly provider: BadgeProvider;
  readonly username: string | null;
  readonly displayName: string | null;
}

interface SuggesterIdentityShape {
  readonly userId: string | null;
  readonly walletAddress: string;
  readonly displayName: string | null;
  readonly handle: string | null;
  readonly avatarUrl: string | null;
  readonly avatarNsfw: boolean;
  readonly avatarPreferences: { readonly rasterise: boolean };
  readonly connectedProviders: readonly ProviderBadgeShape[];
}

function anonymousSuggester(walletAddress: string): SuggesterIdentityShape {
  return {
    userId: null,
    walletAddress,
    displayName: null,
    handle: null,
    avatarUrl: null,
    avatarNsfw: false,
    avatarPreferences: { rasterise: true },
    connectedProviders: [],
  };
}

export interface BuiltOutcomeSuggestionRow {
  readonly id: string;
  readonly marketPubkey: string;
  readonly proposalPubkey: string;
  readonly suggesterPubkey: string;
  readonly suggester: SuggesterIdentityShape;
  readonly label: string;
  readonly ticker: string;
  readonly imageUrl: string;
  readonly reason: string | null;
  readonly upvotes: number;
  readonly downvotes: number;
  readonly score: number;
  readonly status: 'active' | 'promoting' | 'promoted';
  readonly generationMode: 'ai' | 'manual';
  readonly creatorEditedLabel: string | null;
  readonly creatorEditedTicker: string | null;
  readonly creatorEditedImageUrl: string | null;
  readonly promotedOutcomePubkey: string | null;
  readonly outcomeIndex: number | null;
  readonly promotedAt: string | null;
  readonly createdAt: string;
}

const EMPTY_ROW: BuiltOutcomeSuggestionRow = {
  id: '',
  marketPubkey: '',
  proposalPubkey: '',
  suggesterPubkey: '',
  suggester: anonymousSuggester(''),
  label: '',
  ticker: '',
  imageUrl: '',
  reason: null,
  upvotes: 0,
  downvotes: 0,
  score: 0,
  status: 'active',
  generationMode: 'ai',
  creatorEditedLabel: null,
  creatorEditedTicker: null,
  creatorEditedImageUrl: null,
  promotedOutcomePubkey: null,
  outcomeIndex: null,
  promotedAt: null,
  createdAt: '',
};

function readField(raw: Record<string, unknown>, key: string): unknown {
  const nested = raw.payload;
  if (nested !== null && typeof nested === 'object' && key in (nested as object)) {
    return (nested as Record<string, unknown>)[key];
  }
  return raw[key];
}

function readString(raw: Record<string, unknown>, key: string): string {
  const v = readField(raw, key);
  return typeof v === 'string' ? v : '';
}

function readNumber(raw: Record<string, unknown>, key: string): number {
  const v = readField(raw, key);
  if (typeof v === 'bigint') return Number(v);
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/** Proto3 has no null — nullable strings ride the empty-string sentinel
 *  (matching the publisher). Map "" back to `null` so the row shape is
 *  identical to the REST list-item. */
function emptyToNull(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function readProviderBadges(value: unknown): ProviderBadgeShape[] {
  if (!Array.isArray(value)) return [];
  const out: ProviderBadgeShape[] = [];
  for (const entry of value) {
    if (entry === null || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    const provider = e.provider;
    if (provider === 'x' || provider === 'google' || provider === 'apple') {
      out.push({
        provider,
        username: emptyToNull(e.username),
        displayName: emptyToNull(e.displayName),
      });
    }
  }
  return out;
}

/** Project the nested `OutcomeSuggesterIdentity` proto message onto the row's
 *  `suggester`. Falls back to a synthetic anonymous identity (wallet only)
 *  when the field is absent — e.g. an event published before the proto
 *  carried identity — so the next REST refetch still backfills the profile. */
function buildSuggesterFromEvent(
  raw: Record<string, unknown>,
  fallbackWallet: string,
): SuggesterIdentityShape {
  const nested = readField(raw, 'suggester');
  if (nested === null || typeof nested !== 'object') {
    return anonymousSuggester(fallbackWallet);
  }
  const s = nested as Record<string, unknown>;
  const walletAddress =
    typeof s.walletAddress === 'string' && s.walletAddress.length > 0
      ? s.walletAddress
      : fallbackWallet;
  return {
    userId: emptyToNull(s.userId),
    walletAddress,
    displayName: emptyToNull(s.displayName),
    handle: emptyToNull(s.handle),
    avatarUrl: emptyToNull(s.avatarUrl),
    avatarNsfw: s.avatarNsfw === true,
    avatarPreferences: {
      rasterise: typeof s.avatarRasterise === 'boolean' ? s.avatarRasterise : true,
    },
    connectedProviders: readProviderBadges(s.connectedProviders),
  };
}

export function buildOutcomeSuggestionRowFromEvent(
  raw: Record<string, unknown>,
  _subject: string,
  _args?: Record<string, unknown>,
): BuiltOutcomeSuggestionRow {
  const id = readString(raw, 'suggestionId');
  if (id.length === 0) return EMPTY_ROW;

  const marketPubkey = readString(raw, 'marketPubkey');
  const suggesterPubkey = readString(raw, 'suggesterPubkey');
  const label = readString(raw, 'label');
  const ticker = readString(raw, 'ticker');
  const imageUrl = readString(raw, 'imageUrl');
  const rawReason = readString(raw, 'reason');
  const upvotes = readNumber(raw, 'upvotes');
  const downvotes = readNumber(raw, 'downvotes');
  const score = readNumber(raw, 'score');
  const ts = readNumber(raw, 'ts');
  const createdAtMs = readNumber(raw, 'createdAt') || ts;
  const createdAt =
    createdAtMs > 0 ? new Date(createdAtMs).toISOString() : new Date().toISOString();
  const status = readString(raw, 'status');
  const promotedAt = readNumber(raw, 'promotedAt');
  const outcomeIndex = readField(raw, 'outcomeIndex');

  return {
    id,
    marketPubkey,
    proposalPubkey: readString(raw, 'proposalPubkey') || marketPubkey,
    suggesterPubkey,
    // The full UserIdentity rides the creation event (the authenticated
    // POST handler resolves it server-side and the publisher embeds it), so
    // the prepended row renders name/handle/avatar immediately — matching
    // the GET endpoint's `suggester` shape. Falls back to a synthetic
    // anonymous identity only when the field is absent.
    suggester: buildSuggesterFromEvent(raw, suggesterPubkey),
    label,
    ticker,
    imageUrl,
    reason: rawReason.length > 0 ? rawReason : null,
    upvotes,
    downvotes,
    score,
    status: status === 'promoting' || status === 'promoted' ? status : 'active',
    generationMode: readString(raw, 'generationMode') === 'manual' ? 'manual' : 'ai',
    creatorEditedLabel: emptyToNull(readField(raw, 'creatorEditedLabel')),
    creatorEditedTicker: emptyToNull(readField(raw, 'creatorEditedTicker')),
    creatorEditedImageUrl: emptyToNull(readField(raw, 'creatorEditedImageUrl')),
    promotedOutcomePubkey: emptyToNull(readField(raw, 'promotedOutcomePubkey')),
    outcomeIndex: typeof outcomeIndex === 'number' ? outcomeIndex : null,
    promotedAt: promotedAt > 0 ? new Date(promotedAt).toISOString() : null,
    createdAt,
  };
}
